import * as React from 'react';
import { createFileRoute, useNavigate, useRouterState } from '@tanstack/react-router';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import {
  Printer, Filter, X, ArrowUpRight, ArrowDownRight, User2, Calendar, Tag,
  ArrowUp, ArrowDown, ArrowUpDown,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import * as Dialog from '@/components/ui/dialog';
import { DateRangePicker } from '@/components/date-range-picker';
import { RefreshCacheButton } from '@/components/refresh-cache-button';
import { useSalesFilters } from '@/lib/sales-filters';
import {
  getSalesmanReport,
  type SalesmanAgg,
  type SalesmanGrand,
  type SalesmanReportRow,
} from '@/lib/sales-api';
import { fmtINR, fmtNumber, fmtDate } from '@/lib/format';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_app/sales/salesmen')({ component: Page });

// ────────────────────────────────────────────────────────────
// URL-state for filter selections (instant — no server round-trip).
// ────────────────────────────────────────────────────────────
interface ReportFilters {
  salesman: string | null;
  user: string | null;
  subgroup: string | null;
}

function useReportFilters() {
  const search = useRouterState({
    select: (s) => s.location.search as { salesman?: string; user?: string; subgroup?: string },
  });
  const nav = useNavigate();
  const filters: ReportFilters = {
    salesman: search.salesman ?? null,
    user: search.user ?? null,
    subgroup: search.subgroup ?? null,
  };
  const setFilters = React.useCallback(
    (patch: Partial<ReportFilters>) => {
      nav({
        to: '.',
        search: (prev) => {
          const next = { ...(prev as Record<string, unknown>) };
          for (const [k, v] of Object.entries(patch)) {
            if (v === null || v === '') delete next[k];
            else next[k] = v;
          }
          return next;
        },
        replace: true,
      });
    },
    [nav]
  );
  return { ...filters, setFilters };
}

// ────────────────────────────────────────────────────────────
// Client-side filtering + aggregation.
// Filters are applied in-memory; aggregates are recomputed.
// ────────────────────────────────────────────────────────────
function applyFilters(rows: SalesmanReportRow[], f: ReportFilters): SalesmanReportRow[] {
  if (!f.salesman && !f.user && !f.subgroup) return rows;
  return rows.filter((r) => {
    if (f.salesman && r.salesmanId !== f.salesman) return false;
    if (f.user && r.user !== f.user) return false;
    if (f.subgroup && r.subgroup !== f.subgroup) return false;
    return true;
  });
}

function aggregate(rows: SalesmanReportRow[]): { perSalesman: SalesmanAgg[]; grand: SalesmanGrand } {
  const map = new Map<string, SalesmanAgg & { _brands: Set<string>; _arts: Set<string> }>();
  for (const r of rows) {
    const key = r.salesmanId || '—';
    let s = map.get(key);
    if (!s) {
      s = {
        salesmanId: r.salesmanId,
        name: r.salesmanName || r.salesmanId || '—',
        qtySold: 0, amtSold: 0,
        qtyReturn: 0, amtReturn: 0,
        netQty: 0, netAmt: 0,
        distinctBrands: 0, distinctArticles: 0,
        brandCommission: 0,
        solePieceCommission: 0,
        solePieceQty: 0,
        pctCommission: 0,
        commPercent: r.commPercent || 0,
        commission: 0,
        qtyUnbilled: 0, amtUnbilled: 0,
        _brands: new Set(),
        _arts: new Set(),
      };
      map.set(key, s);
    }
    // All sale scans (billed + unbilled) count toward the salesman's totals —
    // unbilled = "estimate" / scanned-but-not-yet-committed, still attributable
    // activity. Track unbilled separately for the hint banner so it's visible.
    if (r.kind === 'sale') {
      s.qtySold += 1;
      s.amtSold += r.price;
      if (!r.billed) {
        s.qtyUnbilled += 1;
        s.amtUnbilled += r.price;
      }
    } else {
      s.qtyReturn += 1;
      s.amtReturn += -r.price; // price is negative for returns
    }
    s.brandCommission += r.commission; // per-line brand commission
    // Sole piece is a slice of brandCommission, not an addition to it — the
    // grand total was always right, it just had no line of its own.
    if (r.solePiece) {
      s.solePieceCommission += r.commission;
      s.solePieceQty += 1;
    }
    // commPercent is per-salesman, not per-row; trust the first non-zero we see
    if (r.commPercent && !s.commPercent) s.commPercent = r.commPercent;
    if (r.brand) s._brands.add(r.brand);
    if (r.artNo) s._arts.add(r.artNo);
  }
  const perSalesman: SalesmanAgg[] = [];
  for (const s of map.values()) {
    s.netQty = s.qtySold - s.qtyReturn;
    s.netAmt = s.amtSold - s.amtReturn;
    s.distinctBrands = s._brands.size;
    s.distinctArticles = s._arts.size;
    // Percentage commission applies to net (sales − returns), integer-truncated.
    s.pctCommission = Math.floor((s.commPercent * s.netAmt) / 100);
    s.commission = s.brandCommission + s.pctCommission;
    const { _brands, _arts, ...clean } = s;
    void _brands; void _arts;
    perSalesman.push(clean);
  }
  perSalesman.sort((a, b) => b.netAmt - a.netAmt);
  const grand: SalesmanGrand = perSalesman.reduce(
    (acc, p) => ({
      qtySold: acc.qtySold + p.qtySold,
      amtSold: acc.amtSold + p.amtSold,
      qtyReturn: acc.qtyReturn + p.qtyReturn,
      amtReturn: acc.amtReturn + p.amtReturn,
      netQty: acc.netQty + p.netQty,
      netAmt: acc.netAmt + p.netAmt,
      brandCommission: acc.brandCommission + p.brandCommission,
      pctCommission: acc.pctCommission + p.pctCommission,
      commission: acc.commission + p.commission,
      solePieceCommission: acc.solePieceCommission + p.solePieceCommission,
      solePieceQty: acc.solePieceQty + p.solePieceQty,
      qtyUnbilled: acc.qtyUnbilled + p.qtyUnbilled,
      amtUnbilled: acc.amtUnbilled + p.amtUnbilled,
    }),
    { qtySold: 0, amtSold: 0, qtyReturn: 0, amtReturn: 0, netQty: 0, netAmt: 0, brandCommission: 0, solePieceCommission: 0, solePieceQty: 0, pctCommission: 0, commission: 0, qtyUnbilled: 0, amtUnbilled: 0 }
  );
  return { perSalesman, grand };
}

function deriveOptions(rows: SalesmanReportRow[]) {
  const salesmenMap = new Map<string, string>();
  const users = new Set<string>();
  const subs = new Set<string>();
  for (const r of rows) {
    if (r.salesmanId && !salesmenMap.has(r.salesmanId)) {
      salesmenMap.set(r.salesmanId, r.salesmanName || r.salesmanId);
    }
    if (r.user) users.add(r.user);
    if (r.subgroup) subs.add(r.subgroup);
  }
  return {
    salesmen: [...salesmenMap.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name)),
    users: [...users].sort(),
    subgroups: [...subs].sort(),
  };
}

// ────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────
function Page() {
  const { from, to, store, setFilters: setShared } = useSalesFilters();
  const { salesman, user, subgroup, setFilters } = useReportFilters();
  const [filterOpen, setFilterOpen] = React.useState(false);
  const [showCount, setShowCount] = React.useState(200);

  // Covered-range cache: the query fetches a range that's expanded on demand
  // and never shrunk. Picking a subset of an already-fetched range filters
  // client-side without a DB hit. Resets whenever store changes.
  const [covered, setCovered] = React.useState<{ from: string; to: string; store: string | null }>({
    from, to, store,
  });
  React.useEffect(() => {
    setCovered((prev) => {
      if (prev.store !== store) return { from, to, store };
      const newFrom = from < prev.from ? from : prev.from;
      const newTo = to > prev.to ? to : prev.to;
      if (newFrom === prev.from && newTo === prev.to) return prev;
      return { from: newFrom, to: newTo, store };
    });
  }, [from, to, store]);

  const q = useQuery({
    queryKey: ['sales:salesman-report', covered.from, covered.to, covered.store],
    queryFn: () =>
      getSalesmanReport({ from: covered.from, to: covered.to, store: covered.store }),
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });

  // Slice fetched rows to the user's current range — instant on every narrow.
  const allRows = React.useMemo(() => {
    const rows = q.data?.rows ?? [];
    if (!rows.length) return rows;
    return rows.filter((r) => r.date >= from && r.date <= to);
  }, [q.data, from, to]);
  const filterState: ReportFilters = { salesman, user, subgroup };

  const filtered = React.useMemo(() => {
    const filteredRows = applyFilters(allRows, filterState);
    // Default = chronological ascending. Desktop table re-sorts on click;
    // Date column = day-buckets only, Time column = chronological.
    return [...filteredRows].sort((a, b) => chronoKey(a) - chronoKey(b));
  }, [allRows, salesman, user, subgroup]);
  const { perSalesman, grand } = React.useMemo(() => aggregate(filtered), [filtered]);
  const options = React.useMemo(() => deriveOptions(allRows), [allRows]);

  // Reset visible row count whenever filters/data change.
  React.useEffect(() => {
    setShowCount(200);
  }, [salesman, user, subgroup, from, to, store]);

  const activeFilterCount = [salesman, user, subgroup].filter(Boolean).length;
  const visibleRows = filtered.slice(0, showCount);

  return (
    <div className="relative space-y-6 pb-24 print:space-y-4 print:pb-0">
      {/* Top fetch indicator */}
      {q.isFetching && (
        <div className="pointer-events-none fixed inset-x-0 top-0 z-50 h-0.5 print:hidden">
          <div className="h-full w-full origin-left animate-[indeterminate_1.4s_ease-in-out_infinite] bg-copper" />
        </div>
      )}

      {/* ── Header / actions ──────────────────────────── */}
      <header className="flex flex-wrap items-end justify-between gap-3 print:mb-2">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            Daleep · Sales
          </p>
          <h1 className="font-display text-3xl font-medium tracking-tight text-ink sm:text-4xl">
            Salesman Report
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {fmtDate(from)} → {fmtDate(to)}
            {store ? <span className="text-ink"> · {store}</span> : ' · all stores'}
            {q.data?.truncated && (
              <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-900">
                truncated to {fmtNumber(allRows.length)} rows
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 print:hidden">
          <RefreshCacheButton
            from={from} to={to} store={store} which="salesman"
            invalidateKeys={[['sales:salesman-report']]}
          />
          <Button
            variant="outline" size="sm"
            onClick={() => setFilterOpen(true)} className="lg:hidden"
          >
            <Filter className="mr-2 h-4 w-4" /> Filters
            {activeFilterCount > 0 && (
              <span className="ml-2 rounded-full bg-copper px-1.5 text-[10px] font-bold text-white">
                {activeFilterCount}
              </span>
            )}
          </Button>
          <Button
            size="sm" onClick={() => window.print()}
            className="bg-ink text-white hover:bg-ink/90"
          >
            <Printer className="mr-2 h-4 w-4" /> Download PDF
          </Button>
        </div>
      </header>

      {/* ── Filter bar (desktop) ──────────────────────── */}
      <div className="hidden lg:block print:hidden">
        <FilterBar
          from={from} to={to}
          onDate={(r) => setShared({ from: r.from, to: r.to })}
          salesman={salesman} user={user} subgroup={subgroup}
          options={options}
          onChange={setFilters}
        />
      </div>
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap gap-1.5 lg:hidden print:hidden">
          {salesman && (
            <Chip onRemove={() => setFilters({ salesman: null })}>
              Salesman: {options.salesmen.find((s) => s.id === salesman)?.name ?? salesman}
            </Chip>
          )}
          {user && <Chip onRemove={() => setFilters({ user: null })}>User: {user}</Chip>}
          {subgroup && (
            <Chip onRemove={() => setFilters({ subgroup: null })}>Subgroup: {subgroup}</Chip>
          )}
        </div>
      )}

      {q.isLoading && !q.data && <SkeletonHero />}
      {q.error && (
        <p className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          {(q.error as Error).message}
        </p>
      )}

      {q.data && (
        <div className={cn('space-y-6 transition-opacity', q.isFetching && 'opacity-60')}>
          {/* ── KPI Hero ──────────────────────────────── */}
          <KpiHero
            netQty={grand.netQty}
            netAmt={grand.netAmt}
            qtySold={grand.qtySold}
            amtSold={grand.amtSold}
            qtyReturn={grand.qtyReturn}
            amtReturn={grand.amtReturn}
            commission={grand.commission}
            solePieceCommission={grand.solePieceCommission}
            solePieceQty={grand.solePieceQty}
            qtyUnbilled={grand.qtyUnbilled}
            amtUnbilled={grand.amtUnbilled}
          />

          {/* ── Leaderboard ──────────────────────────── */}
          {perSalesman.length > 0 && (
            <section aria-label="Per-salesman breakdown" className="space-y-3 print:break-inside-avoid">
              <SectionTitle>Per-Salesman</SectionTitle>
              <Leaderboard data={perSalesman} max={Math.max(grand.netAmt, 1)} />
            </section>
          )}

          {/* ── Detail rows ──────────────────────────── */}
          <section aria-label="Detail rows" className="space-y-3 print:break-before">
            <div className="flex items-baseline justify-between">
              <SectionTitle>Detail · {fmtNumber(filtered.length)} rows</SectionTitle>
              <p className="text-xs text-muted-foreground">
                Showing {Math.min(showCount, filtered.length)} of {fmtNumber(filtered.length)}
              </p>
            </div>
            <DetailTable rows={visibleRows} />
            <DetailCards rows={visibleRows} />
            {filtered.length > showCount && (
              <div className="flex justify-center print:hidden">
                <Button variant="outline" size="sm" onClick={() => setShowCount((c) => c + 500)}>
                  Show 500 more
                </Button>
              </div>
            )}
          </section>
        </div>
      )}

      {/* ── Mobile filter sheet ───────────────────────── */}
      <Dialog.Root open={filterOpen} onOpenChange={setFilterOpen}>
        <Dialog.Content className="max-h-[85vh] overflow-y-auto sm:max-w-md">
          <Dialog.Title className="font-display text-xl">Filters</Dialog.Title>
          <FilterBar
            from={from} to={to}
            onDate={(r) => setShared({ from: r.from, to: r.to })}
            salesman={salesman} user={user} subgroup={subgroup}
            options={options}
            onChange={setFilters}
            stacked
          />
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="ghost" size="sm"
              onClick={() => {
                setFilters({ salesman: null, user: null, subgroup: null });
                setFilterOpen(false);
              }}
            >
              Clear all
            </Button>
            <Button size="sm" onClick={() => setFilterOpen(false)}>Apply</Button>
          </div>
        </Dialog.Content>
      </Dialog.Root>

      {/* indeterminate keyframes */}
      <style>{`
        @keyframes indeterminate {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(0%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// FilterBar
// ────────────────────────────────────────────────────────────
function FilterBar({
  from, to, onDate,
  salesman, user, subgroup,
  options, onChange,
  stacked = false,
}: {
  from: string; to: string;
  onDate: (r: { from: string; to: string }) => void;
  salesman: string | null; user: string | null; subgroup: string | null;
  options: { salesmen: { id: string; name: string }[]; users: string[]; subgroups: string[] };
  onChange: (patch: Partial<ReportFilters>) => void;
  stacked?: boolean;
}) {
  return (
    <div
      className={cn(
        'rounded-xl border border-border/80 bg-sand/50 p-3 shadow-sm',
        stacked ? 'space-y-3' : 'flex flex-wrap items-end gap-2'
      )}
    >
      <div className={cn(stacked ? '' : 'min-w-[280px]')}>
        <label className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          Date range
        </label>
        <DateRangePicker from={from} to={to} onChange={onDate} />
      </div>
      <NativeSelect
        label="Salesman" icon={<User2 className="h-3.5 w-3.5" />}
        value={salesman ?? ''}
        onChange={(v) => onChange({ salesman: v || null })}
        placeholder="All salesmen"
        options={options.salesmen.map((s) => ({ value: s.id, label: s.name }))}
        stacked={stacked}
      />
      <NativeSelect
        label="POS user" icon={<User2 className="h-3.5 w-3.5" />}
        value={user ?? ''}
        onChange={(v) => onChange({ user: v || null })}
        placeholder="All users"
        options={options.users.map((u) => ({ value: u, label: u }))}
        stacked={stacked}
      />
      <NativeSelect
        label="Subgroup" icon={<Tag className="h-3.5 w-3.5" />}
        value={subgroup ?? ''}
        onChange={(v) => onChange({ subgroup: v || null })}
        placeholder="All subgroups"
        options={options.subgroups.map((s) => ({ value: s, label: s }))}
        stacked={stacked}
      />
    </div>
  );
}

function NativeSelect({
  label, icon, value, onChange, placeholder, options, stacked,
}: {
  label: string;
  icon?: React.ReactNode;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  options: { value: string; label: string }[];
  stacked?: boolean;
}) {
  return (
    <div className={cn(stacked ? '' : 'min-w-[160px]')}>
      <label className="mb-1 flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        {icon}
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// KPI Hero
// ────────────────────────────────────────────────────────────
function KpiHero({
  netQty, netAmt, qtySold, amtSold, qtyReturn, amtReturn, commission,
  solePieceCommission = 0, solePieceQty = 0,
  qtyUnbilled, amtUnbilled,
}: {
  netQty: number; netAmt: number;
  qtySold: number; amtSold: number;
  qtyReturn: number; amtReturn: number;
  commission: number;
  solePieceCommission?: number;
  solePieceQty?: number;
  qtyUnbilled: number; amtUnbilled: number;
}) {
  return (
    <section className="overflow-hidden rounded-2xl border border-border/80 bg-gradient-to-br from-sand to-white p-4 shadow-sm sm:p-6 print:break-inside-avoid print:bg-white print:shadow-none">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 sm:gap-6">
        <HeroTile label="Net amount" value={fmtINR(netAmt)} accent="copper" big />
        <HeroTile label="Net qty" value={fmtNumber(netQty)} big />
        <HeroTile
          label="Sales" value={fmtINR(amtSold)}
          hint={`${fmtNumber(qtySold)} units`}
          icon={<ArrowUpRight className="h-3.5 w-3.5 text-emerald-700" />}
        />
        <HeroTile
          label="Returns" value={fmtINR(amtReturn)}
          hint={`${fmtNumber(qtyReturn)} units`}
          accent="rose"
          icon={<ArrowDownRight className="h-3.5 w-3.5 text-rose-deep" />}
        />
      </div>
      {(commission > 0 || qtyUnbilled > 0) && (
        <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-border/60 pt-3 text-xs text-muted-foreground">
          {commission > 0 && (
            <span>
              Commission earned ·{' '}
              <span className="font-mono-num text-ink">{fmtINR(commission)}</span>
            </span>
          )}
          {solePieceCommission > 0 && (
            <span>
              <span className="mr-1.5 inline-block rounded-full bg-violet-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-violet-900">
                Of which sole piece
              </span>
              {fmtNumber(solePieceQty)} scan{solePieceQty === 1 ? '' : 's'} ·{' '}
              <span className="font-mono-num text-ink">{fmtINR(solePieceCommission)}</span>
              <span className="ml-1 italic">dead-stock incentive</span>
            </span>
          )}
          {qtyUnbilled > 0 && (
            <span>
              <span className="mr-1.5 inline-block rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-900">
                Of which unbilled
              </span>
              {fmtNumber(qtyUnbilled)} scan{qtyUnbilled === 1 ? '' : 's'} ·{' '}
              <span className="font-mono-num text-ink">{fmtINR(amtUnbilled)}</span>
              <span className="ml-1 italic">scanned but not yet on a committed bill</span>
            </span>
          )}
        </div>
      )}
    </section>
  );
}

function HeroTile({
  label, value, hint, icon, accent, big,
}: {
  label: string;
  value: string;
  hint?: string;
  icon?: React.ReactNode;
  accent?: 'copper' | 'rose';
  big?: boolean;
}) {
  const accentClass =
    accent === 'copper' ? 'text-copper'
    : accent === 'rose' ? 'text-rose-deep'
    : 'text-ink';
  return (
    <div className="space-y-1">
      <p className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
        {icon}
        {label}
      </p>
      <p
        className={cn(
          'font-display font-medium leading-[1.05]',
          big ? 'text-3xl sm:text-5xl' : 'text-2xl sm:text-3xl',
          accentClass
        )}
      >
        {value}
      </p>
      {hint && <p className="font-mono-num text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Leaderboard
// ────────────────────────────────────────────────────────────
function Leaderboard({ data, max }: { data: SalesmanAgg[]; max: number }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {data.map((s) => (
        <SalesmanCard key={s.salesmanId || s.name} agg={s} max={max} />
      ))}
    </div>
  );
}

function SalesmanCard({ agg, max }: { agg: SalesmanAgg; max: number }) {
  const pct = max > 0 ? Math.max(0.04, agg.netAmt / max) : 0;
  return (
    <Card className="overflow-hidden border-border/70 transition-colors hover:border-copper/60 print:break-inside-avoid">
      <CardContent className="space-y-3 p-4">
        <div className="flex items-baseline justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate font-display text-lg font-medium text-ink">{agg.name}</p>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
              {agg.salesmanId || '—'}
            </p>
          </div>
          <div className="text-right">
            <p className="font-mono-num text-base font-semibold text-copper">
              {fmtINR(agg.netAmt)}
            </p>
            <p className="font-mono-num text-[11px] text-muted-foreground">
              {fmtNumber(agg.netQty)} pcs net
            </p>
          </div>
        </div>
        <div className="h-1 w-full overflow-hidden rounded-full bg-copper-soft">
          <div
            className="h-full rounded-full bg-copper transition-[width] duration-500"
            style={{ width: `${pct * 100}%` }}
          />
        </div>
        <dl className="grid grid-cols-3 gap-2 text-[11px]">
          <Stat label="Sales" value={`${fmtNumber(agg.qtySold)} · ${fmtINR(agg.amtSold)}`} />
          <Stat
            label="Returns"
            value={`${fmtNumber(agg.qtyReturn)} · ${fmtINR(agg.amtReturn)}`}
            tone="rose"
          />
          <Stat
            label="Total Comm."
            value={fmtINR(agg.commission)}
            hint={
              agg.commPercent
                ? `${fmtINR(agg.brandCommission)} brand + ${fmtINR(agg.pctCommission)} (${agg.commPercent}% × net)`
                : `${fmtINR(agg.brandCommission)} brand`
            }
          />
          <Stat label="Brands" value={fmtNumber(agg.distinctBrands)} />
          <Stat label="Articles" value={fmtNumber(agg.distinctArticles)} />
        </dl>
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, tone, hint }: { label: string; value: string; tone?: 'rose'; hint?: string }) {
  return (
    <div className="rounded-md bg-muted/50 px-2 py-1.5" title={hint}>
      <p className="text-[9px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p
        className={cn(
          'truncate font-mono-num text-[11px] font-medium',
          tone === 'rose' ? 'text-rose-deep' : 'text-ink'
        )}
      >
        {value}
      </p>
      {hint && <p className="truncate font-mono-num text-[9px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Detail: dense table (desktop) + cards (mobile)
// ────────────────────────────────────────────────────────────
type SortKey =
  | 'status' | 'salesman' | 'date' | 'time' | 'barcode'
  | 'item' | 'brand' | 'artNo' | 'color' | 'size'
  | 'price' | 'user' | 'commission' | 'subgroup';

// Times come from the DB as "HH:MM:SS AM/PM" (12-hour). String-sort breaks
// across the AM/PM boundary (e.g. "01:00:00 PM" < "11:00:00 AM" lexically),
// so we project to seconds-since-midnight for any time-based comparison.
function timeToSeconds(t: string | null | undefined): number {
  if (!t) return 0;
  const m = t.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i);
  if (!m) return 0;
  let h = parseInt(m[1] ?? '0', 10);
  const min = parseInt(m[2] ?? '0', 10);
  const s = parseInt(m[3] ?? '0', 10);
  const ampm = (m[4] ?? '').toUpperCase();
  if (ampm === 'AM' && h === 12) h = 0;
  else if (ampm === 'PM' && h !== 12) h += 12;
  return h * 3600 + min * 60 + s;
}

// "YYYY-MM-DD" → 20260415 (numeric, lossless, sortable).
function dateToInt(d: string | null | undefined): number {
  if (!d) return 0;
  const m = d.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return 0;
  return parseInt((m[1] ?? '') + (m[2] ?? '') + (m[3] ?? ''), 10);
}

// Composite chronological key (date * 86400 + seconds-since-midnight). Used
// for the Time column so that, in multi-day ranges, "sort by Time" means
// "earliest scan first → latest scan last" instead of mixing 9 AM rows from
// different days together. See discussion in commit message.
function chronoKey(r: SalesmanReportRow): number {
  return dateToInt(r.date) * 100_000 + timeToSeconds(r.time);
}

const SORT_ACCESSORS: Record<SortKey, (r: SalesmanReportRow) => string | number> = {
  // Status: returns < estimates < billed (returns first, billed last)
  status: (r) => r.kind === 'return' ? 0 : (r.billed ? 2 : 1),
  salesman: (r) => (r.salesmanName || r.salesmanId || '').toLowerCase(),
  date: (r) => dateToInt(r.date),
  // Time = composite (date, time-of-day). In a single-day range this collapses
  // to time-of-day; in multi-day it gives true chronological order.
  time: (r) => chronoKey(r),
  barcode: (r) => r.barcode,
  item: (r) => (r.itemName || '').toLowerCase(),
  brand: (r) => (r.brand || '').toLowerCase(),
  artNo: (r) => r.artNo || '',
  color: (r) => (r.color || '').toLowerCase(),
  size: (r) => r.size || '',
  price: (r) => r.price,
  user: (r) => r.user || '',
  commission: (r) => r.commission,
  subgroup: (r) => r.subgroup || '',
};

function sortRows(rows: SalesmanReportRow[], key: SortKey, dir: 'asc' | 'desc'): SalesmanReportRow[] {
  const accessor = SORT_ACCESSORS[key];
  const sign = dir === 'asc' ? 1 : -1;
  // Tiebreaker = chronological. EXCEPT when the active sort key IS date —
  // then we want a "group by day" view with within-day order untouched
  // (relies on Array.prototype.sort being stable, which it is in modern JS).
  const useChronoTiebreaker = key !== 'date' && key !== 'time';
  return [...rows].sort((a, b) => {
    const av = accessor(a);
    const bv = accessor(b);
    if (av < bv) return -1 * sign;
    if (av > bv) return  1 * sign;
    if (useChronoTiebreaker) {
      const ak = chronoKey(a);
      const bk = chronoKey(b);
      if (ak !== bk) return ak - bk;
    }
    return 0;
  });
}

function DetailTable({ rows }: { rows: SalesmanReportRow[] }) {
  const [sortKey, setSortKey] = React.useState<SortKey>('date');
  const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('asc');
  const sorted = React.useMemo(() => sortRows(rows, sortKey, sortDir), [rows, sortKey, sortDir]);

  const onSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc'); // default ascending on new column
    }
  };

  return (
    <div className="hidden overflow-x-auto rounded-lg border border-border/70 bg-background sm:block print:block print:rounded-none print:border-black">
      <table className="w-full text-[12px]">
        <thead className="bg-sand/70 text-[10px] uppercase tracking-wider text-muted-foreground print:bg-white">
          <tr>
            <SortableTh sortKey="status" current={sortKey} dir={sortDir} onClick={onSort}>Status</SortableTh>
            <SortableTh sortKey="salesman" current={sortKey} dir={sortDir} onClick={onSort}>Salesman</SortableTh>
            <SortableTh sortKey="date" current={sortKey} dir={sortDir} onClick={onSort}>Date</SortableTh>
            <SortableTh sortKey="time" current={sortKey} dir={sortDir} onClick={onSort}>Time</SortableTh>
            <SortableTh sortKey="barcode" current={sortKey} dir={sortDir} onClick={onSort}>Barcode</SortableTh>
            <SortableTh sortKey="item" current={sortKey} dir={sortDir} onClick={onSort}>Item</SortableTh>
            <SortableTh sortKey="brand" current={sortKey} dir={sortDir} onClick={onSort}>Brand</SortableTh>
            <SortableTh sortKey="artNo" current={sortKey} dir={sortDir} onClick={onSort}>Art No.</SortableTh>
            <SortableTh sortKey="color" current={sortKey} dir={sortDir} onClick={onSort}>Color</SortableTh>
            <SortableTh sortKey="size" current={sortKey} dir={sortDir} onClick={onSort}>Size</SortableTh>
            <SortableTh sortKey="price" current={sortKey} dir={sortDir} onClick={onSort} align="right">Price</SortableTh>
            <SortableTh sortKey="user" current={sortKey} dir={sortDir} onClick={onSort}>User</SortableTh>
            <SortableTh sortKey="commission" current={sortKey} dir={sortDir} onClick={onSort} align="right">Comm.</SortableTh>
            <SortableTh sortKey="subgroup" current={sortKey} dir={sortDir} onClick={onSort}>Subgroup</SortableTh>
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && (
            <tr>
              <td colSpan={14} className="p-8 text-center text-sm text-muted-foreground">
                No rows match the current filters.
              </td>
            </tr>
          )}
          {sorted.map((r, i) => (
            <tr
              key={`${r.kind}-${r.billNo}-${r.barcode}-${r.time}-${i}`}
              className={cn(
                'border-t border-border/40 transition-colors hover:bg-sand/40',
                r.kind === 'return' && 'bg-rose-soft/40',
                r.kind === 'sale' && !r.billed && 'bg-amber-50/60'
              )}
            >
              <Td><BilledBadge row={r} /></Td>
              <Td className="font-medium">{r.salesmanName || r.salesmanId || '—'}</Td>
              <Td className="font-mono-num text-muted-foreground">{fmtDate(r.date)}</Td>
              <Td className="font-mono-num text-muted-foreground">{r.time}</Td>
              <Td className="font-mono-num text-[11px]">{r.barcode}</Td>
              <Td>{r.itemName}</Td>
              <Td className="text-muted-foreground">{r.brand}</Td>
              <Td className="font-mono-num">{r.artNo}</Td>
              <Td className="text-muted-foreground">{r.color}</Td>
              <Td className="font-mono-num">{r.size}</Td>
              <Td
                align="right"
                className={cn(
                  'font-mono-num font-semibold',
                  r.kind === 'return' ? 'text-rose-deep' : 'text-ink'
                )}
              >
                {fmtINR(r.price)}
              </Td>
              <Td className="font-mono-num text-[11px] text-muted-foreground">{r.user}</Td>
              <Td align="right" className="font-mono-num text-muted-foreground">
                {r.commission > 0 ? fmtINR(r.commission) : '—'}
              </Td>
              <Td className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {r.subgroup}
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      className={cn(
        'whitespace-nowrap px-2.5 py-2 font-medium',
        align === 'right' ? 'text-right' : 'text-left'
      )}
    >
      {children}
    </th>
  );
}

function SortableTh({
  children, sortKey, current, dir, onClick, align = 'left',
}: {
  children: React.ReactNode;
  sortKey: SortKey;
  current: SortKey;
  dir: 'asc' | 'desc';
  onClick: (key: SortKey) => void;
  align?: 'left' | 'right';
}) {
  const active = current === sortKey;
  const Icon = !active ? ArrowUpDown : (dir === 'asc' ? ArrowUp : ArrowDown);
  return (
    <th
      className={cn(
        'whitespace-nowrap px-2.5 py-2 font-medium print:cursor-default',
        align === 'right' ? 'text-right' : 'text-left'
      )}
    >
      <button
        type="button"
        onClick={() => onClick(sortKey)}
        className={cn(
          'inline-flex items-center gap-1 transition-colors hover:text-ink print:pointer-events-none',
          align === 'right' && 'flex-row-reverse',
          active && 'text-ink'
        )}
      >
        {children}
        <Icon
          className={cn(
            'h-3 w-3 shrink-0 transition-opacity print:hidden',
            active ? 'opacity-100 text-copper' : 'opacity-30'
          )}
        />
      </button>
    </th>
  );
}
function Td({
  children, align = 'left', className,
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
  className?: string;
}) {
  return (
    <td
      className={cn(
        'whitespace-nowrap px-2.5 py-1.5',
        align === 'right' ? 'text-right' : 'text-left',
        className
      )}
    >
      {children}
    </td>
  );
}

function BilledBadge({ row }: { row: SalesmanReportRow }) {
  if (row.kind === 'return') {
    return (
      <span className="inline-block rounded-full bg-rose-soft px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider text-rose-deep">
        Return
      </span>
    );
  }
  if (row.billed) {
    return (
      <span className="inline-block rounded-full bg-emerald-50 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider text-emerald-800">
        Billed
      </span>
    );
  }
  return (
    <span className="inline-block rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider text-amber-900" title="Scanned but never committed to a bill">
      Estimate
    </span>
  );
}

function DetailCards({ rows }: { rows: SalesmanReportRow[] }) {
  return (
    <div className="space-y-2 sm:hidden print:hidden">
      {rows.length === 0 && (
        <p className="rounded-lg border border-dashed border-border/60 p-6 text-center text-sm text-muted-foreground">
          No rows match the current filters.
        </p>
      )}
      {rows.map((r, i) => (
        <div
          key={`${r.kind}-${r.billNo}-${r.barcode}-${r.time}-${i}`}
          className={cn(
            'rounded-lg border border-border/70 bg-background p-3 shadow-sm',
            r.kind === 'return' && 'border-rose-deep/30 bg-rose-soft/30',
            r.kind === 'sale' && !r.billed && 'border-amber-300 bg-amber-50/60'
          )}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="mb-1 flex items-center gap-1.5">
                <BilledBadge row={r} />
              </div>
              <p className="truncate text-sm font-medium text-ink">{r.itemName}</p>
              <p className="font-mono-num text-[11px] text-muted-foreground">
                {r.barcode} · {r.brand}
              </p>
              {r.artNo && (
                <p className="font-mono-num text-[11px] text-muted-foreground">
                  art {r.artNo} · {r.color}
                  {r.size && ` · sz ${r.size}`}
                </p>
              )}
            </div>
            <div className="text-right">
              <p
                className={cn(
                  'font-mono-num text-base font-semibold',
                  r.kind === 'return' ? 'text-rose-deep' : 'text-ink'
                )}
              >
                {fmtINR(r.price)}
              </p>
              <p className="font-mono-num text-[10px] text-muted-foreground">
                {r.kind === 'return' ? 'return' : 'sale'}
              </p>
            </div>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border/40 pt-2 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1">
              <User2 className="h-3 w-3" /> {r.salesmanName || '—'}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" /> {fmtDate(r.date)} {r.time}
            </span>
            {r.subgroup && (
              <span className="flex items-center gap-1">
                <Tag className="h-3 w-3" /> {r.subgroup}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// Bits
// ────────────────────────────────────────────────────────────
function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="font-display text-lg font-medium text-ink sm:text-xl">
      <span className="mr-3 inline-block h-px w-8 -translate-y-1.5 bg-copper align-middle" />
      {children}
    </h2>
  );
}

function Chip({
  children, onRemove,
}: {
  children: React.ReactNode;
  onRemove: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onRemove}
      className="inline-flex items-center gap-1 rounded-full border border-border/80 bg-sand px-2.5 py-1 text-[11px] font-medium text-ink transition-colors hover:border-copper hover:text-copper"
    >
      {children}
      <X className="h-3 w-3" />
    </button>
  );
}

function SkeletonHero() {
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 sm:gap-6">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="space-y-2">
          <div className="h-3 w-16 rounded bg-muted animate-pulse" />
          <div className="h-10 w-24 rounded bg-muted animate-pulse" />
        </div>
      ))}
    </div>
  );
}
